// trivy-operator writes one VulnerabilityReport per workload container, so a
// multi-container workload produces several reports.

export type TrivySeverity =
  | 'CRITICAL'
  | 'HIGH'
  | 'MEDIUM'
  | 'LOW'
  | 'UNKNOWN'
  | 'NONE';

// UNKNOWN is unranked upstream, not "below LOW".
export const TRIVY_SEVERITIES: ReadonlyArray<TrivySeverity> = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'UNKNOWN',
];

export const TRIVY_LABELS = {
  resourceName: 'trivy-operator.resource.name',
  resourceNamespace: 'trivy-operator.resource.namespace',
  resourceKind: 'trivy-operator.resource.kind',
  containerName: 'trivy-operator.container.name',
} as const;

export type Vulnerability = {
  vulnerabilityID: string;
  severity: TrivySeverity;
  score?: number;
  title: string;
  resource: string;
  installedVersion: string;
  // Empty string when upstream has published no fix.
  fixedVersion: string;
  primaryLink: string;
  publishedDate?: string;
};

export type VulnerabilitySummary = {
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  unknownCount: number;
  noneCount: number;
};

export type VulnerabilityReport = {
  metadata: {
    name: string;
    namespace: string;
    labels?: Record<string, string>;
  };
  report: {
    artifact: { repository: string; tag?: string; digest?: string };
    scanner?: { name: string; version: string };
    summary: VulnerabilitySummary;
    updateTimestamp?: string;
    vulnerabilities?: Array<Vulnerability>;
  };
};

export type VulnerabilityReportList = {
  items: Array<VulnerabilityReport>;
};

// Kubernetes server-side Table output. The CRD's additionalPrinterColumns
// project the severity counts, which is 116 KB against 4.5 MB for the full
// reports. Cells are positional, so resolve them by column name, not index.
export type TableColumnDefinition = {
  name: string;
  type: string;
};

export type TableRow = {
  cells: Array<unknown>;
  // Present only when the request asks for includeObject=Metadata.
  object?: {
    metadata: {
      name: string;
      namespace: string;
      creationTimestamp?: string;
      labels?: Record<string, string>;
    };
  };
};

export type VulnerabilityReportTable = {
  columnDefinitions: Array<TableColumnDefinition>;
  rows?: Array<TableRow>;
};

export const TABLE_COLUMNS = {
  repository: 'Repository',
  tag: 'Tag',
  age: 'Age',
} as const;

export const SEVERITY_TABLE_COLUMN: Record<TrivySeverity, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  UNKNOWN: 'Unknown',
  NONE: 'None',
};

export const SEVERITY_SUMMARY_FIELD: Record<
  TrivySeverity,
  keyof VulnerabilitySummary
> = {
  CRITICAL: 'criticalCount',
  HIGH: 'highCount',
  MEDIUM: 'mediumCount',
  LOW: 'lowCount',
  UNKNOWN: 'unknownCount',
  NONE: 'noneCount',
};

// Lower is more severe; unknown severities sort last.
export function severityRank(severity: TrivySeverity): number {
  const index = TRIVY_SEVERITIES.indexOf(severity);
  return index === -1 ? TRIVY_SEVERITIES.length : index;
}
